<template>
  <div class="wrapper">
    <data-filter :on-apply-filter="onApplyFilter" hide-report download-excel :on-download-excel="onDownloadExcel"
      :minute=true />

    <!-- start unit selection -->
    <div class="wrapper mt-2">
      <div class="d-flex align-items-center">
        <div class="ml-3">
          <span class="d-block text-muted small text-left font-weight-bold mb-2">Speed Unit</span>
          <select name="" class="form-control" v-model="unit.speed">
            <option value="knot">KNOT</option>
            <option value="km/h">KM/H</option>
          </select>
        </div>
        <div class="ml-3">
          <span class="d-block text-muted small text-left font-weight-bold mb-2">Distance Unit</span>
          <select name="" class="form-control" v-model="unit.distance">
            <option value="nm">NM</option>
            <option value="km">KM</option>
          </select>
        </div>
        <div class="ml-3">
          <span class="d-block text-muted small text-left font-weight-bold mb-2">Sort Data By</span>
          <select name="" class="form-control" v-model="dataSort">
            <option value="latest">Latest</option>
            <option value="oldest">Oldest</option>
          </select>
        </div>
        <div class="ml-3">
          <span class="d-block text-muted small text-left font-weight-bold mb-2">&nbsp;</span>
          <button class="btn btn-mantraz" @click="showShowHideModal">
            Filter Column
          </button>
        </div>
        <div class="ml-3">
          <span class="d-block text-muted small text-left font-weight-bold mb-2">&nbsp;</span>
          <button class="btn btn-mantraz" @click="resetColumns">
            Load Default Column
          </button>
        </div>
      </div>
    </div>
    <!-- end unit selection -->

    <!-- show/hide column modal -->
    <b-modal id="show-hide-column-modal" title="Show/Hide Datalog Column" size="xl">
      <div class="d-flex justify-content-end mb-3 border-bottom pb-3">
        <button class="btn btn-sm btn-mantraz mr-2" @click="showAllColumns">Show All</button>
        <button class="btn btn-sm btn-outline-mantraz" @click="hideAllColumns">Hide All</button>
      </div>

      <div class="row">
        <div v-for="(categoryColumns, categoryKey) in columnsForModal" :key="categoryKey"
          class="col-md-4 col-lg-3 mb-4">
          <div class="d-flex justify-content-between align-items-center border-bottom pb-1 mb-2">
            <h5 class="text-capitalize font-weight-bold mb-0">{{ categoryKey }}</h5>
            <div class="form-check">
              <input type="checkbox" class="form-check-input" :id="`toggle-${categoryKey}`"
                :checked="isCategoryChecked(categoryKey)" @change="toggleCategory(categoryKey)" />
              <label class="form-check-label font-weight-bold small" :for="`toggle-${categoryKey}`">
                Select All
              </label>
            </div>
          </div>
          <div v-for="column in categoryColumns" :key="column.key" class="form-check">
            <input class="form-check-input" type="checkbox" :id="column.key"
              :checked="findColumnInDisplay(column.key).visible" @change="toggleColumnVisibility(column.key)" />
            <label class="form-check-label" :for="column.key">
              {{ column.label }}
            </label>
          </div>
        </div>
      </div>
    </b-modal>
    <!-- ./ show/hide column modal -->

    <template v-if="isLoading">
      <div class="text-center mt-5">
        <b-spinner label="Spinning"></b-spinner>
      </div>
    </template>
    <template v-else-if="currAssetSummary && !isLoading">
      <!-- Data percentage -->
      <div style="padding: 0 10px; margin-top: 10px;">
        <data-percentage />
      </div>
      <!-- ./ Data percentage -->

      <div class="iq-card mt-3">
        <div class="iq-card-body pt-2 pb-5 mb-5">
          <div class="table-responsive">
            <div style="height: 70vh; overflow-y: auto;">
              <table class="table table-striped table-sm table-hover table-bordered text-center">
                <thead class="thead-light">
                  <tr>
                    <th v-for="(col, index) in visibleColumns" :key="col.key"
                      :style="{ minWidth: col.minWidth * 1.5 + 'px' }" draggable="true" @dragstart="onDragStart(index)"
                      @dragover.prevent @drop="onDrop(index)" @dragend="onDragEnd" class="draggable-column py-2 px-2">
                      {{ col.label }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="data in mappedData" :key="data.timestamp">
                    <td v-for="(col, index) in visibleColumns" :key="col.key"
                      :class="index === 0 ? 'sticky-column' : ''">
                      {{ data[col.key] }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <hr />
        </div>
      </div>
    </template>
  </div>
</template>

<script>
/* eslint-disable object-curly-newline */
import moment from 'moment';
import { mapState } from 'vuex';
import DataFilter from '@/components/DataFilter';
import { toHHMMSS } from '@/util/date';
import { buildFmsSeries } from '@/util/fms-summary';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import DataPercentage from '@/components/DataPercentage.vue';

export default {
  name: 'FmsReportDatalog',
  components: {
    DataFilter,
    DataPercentage,
  },
  data() {
    return {
      unit: {
        distance: 'nm',
        speed: 'knot',
      },
      dataSort: 'oldest',
      displayColumns: [],
      defaultColumns: {
        dateTime: [
          { key: 'dateTime', label: 'Date Time', minWidth: 180, visible: true },
        ],
        gps: [
          { key: 'coordinate', label: 'Coordinate', minWidth: 200, visible: true },
          { key: 'heading', label: 'Heading', minWidth: 100, visible: true },
          { key: 'speed', label: 'Speed', minWidth: 100, visible: true },
          { key: 'cruise', label: 'Cruise', minWidth: 100, visible: true },
        ],
        port: [
          { key: 'portRpm', label: 'Port RPM', minWidth: 100, visible: true },
          { key: 'portVolumeFlowrate', label: 'Port Vol. Flowrate', minWidth: 100, visible: true },
          { key: 'portRunningHours', label: 'Port Run. Hours', minWidth: 100, visible: true },
          { key: 'portVolumeInventory', label: 'Port Vol. Inv.', minWidth: 100, visible: true },
          { key: 'portTemperature', label: 'Port Temp.', minWidth: 100, visible: true },
          { key: 'portDensity', label: 'Port Density', minWidth: 100, visible: true },
          { key: 'temperatureExhaustPort', label: 'Port Exhaust Temp.', minWidth: 100, visible: true },
          { key: 'temperatureTankPort', label: 'Port Reservoir Temp.', minWidth: 100, visible: true },
        ],
        starboard: [
          { key: 'starboardRpm', label: 'Starboard RPM', minWidth: 100, visible: true },
          { key: 'starboardVolumeFlowrate', label: 'Starboard Vol. Flowrate', minWidth: 100, visible: true },
          { key: 'starboardRunningHours', label: 'Starboard Run. Hours', minWidth: 100, visible: true },
          { key: 'starboardVolumeInventory', label: 'Starboard Vol. Inv.', minWidth: 100, visible: true },
          { key: 'starboardTemperature', label: 'Starboard Temp.', minWidth: 100, visible: true },
          { key: 'starboardDensity', label: 'Starboard Density', minWidth: 100, visible: true },
          { key: 'temperatureExhaustStarboard', label: 'Starboard Exhaust Temp.', minWidth: 100, visible: true },
          { key: 'temperatureTankStarboard', label: 'Starboard Reservoir Temp.', minWidth: 100, visible: true },
        ],
        ae: [
          { key: 'ae1RunningHours', label: 'AE1 Run. Hours', minWidth: 100, visible: true },
          { key: 'ae1FuelConsumption', label: 'AE1 Fuel Consumption', minWidth: 100, visible: true },
          { key: 'ae2RunningHours', label: 'AE2 Run. Hours', minWidth: 100, visible: true },
          { key: 'ae2FuelConsumption', label: 'AE2 Fuel Consumption', minWidth: 100, visible: true },
          { key: 'ae3RunningHours', label: 'AE3 Run. Hours', minWidth: 100, visible: true },
          { key: 'ae3FuelConsumption', label: 'AE3 Fuel Consumption', minWidth: 100, visible: true },
        ],
      },
      columns: {
        dateTime: [
          { key: 'dateTime', label: 'Date Time', minWidth: 180, visible: true },
        ],
        gps: [
          { key: 'coordinate', label: 'Coordinate', minWidth: 200, visible: true },
          { key: 'heading', label: 'Heading', minWidth: 100, visible: true },
          { key: 'speed', label: 'Speed', minWidth: 100, visible: true },
          { key: 'cruise', label: 'Cruise', minWidth: 100, visible: true },
        ],
        port: [
          { key: 'portRpm', label: 'Port RPM', minWidth: 100, visible: true },
          { key: 'portRunningHours', label: 'Port Run. Hours', minWidth: 100, visible: true },
          { key: 'portVolumeInventory', label: 'Port Vol. Inv.', minWidth: 100, visible: true },
          { key: 'portVolumeFlowrate', label: 'Port Vol. Flowrate', minWidth: 100, visible: true },
          { key: 'portTemperature', label: 'Port Temp.', minWidth: 100, visible: true },
          { key: 'portDensity', label: 'Port Density', minWidth: 100, visible: true },
          { key: 'temperatureExhaustPort', label: 'Port Exhaust Temp.', minWidth: 100, visible: true },
          { key: 'temperatureTankPort', label: 'Port Reservoir Temp.', minWidth: 100, visible: true },
        ],
        starboard: [
          { key: 'starboardRpm', label: 'Starboard RPM', minWidth: 100, visible: true },
          { key: 'starboardRunningHours', label: 'Starboard Run. Hours', minWidth: 100, visible: true },
          { key: 'starboardVolumeInventory', label: 'Starboard Vol. Inv.', minWidth: 100, visible: true },
          { key: 'starboardVolumeFlowrate', label: 'Starboard Vol. Flowrate', minWidth: 100, visible: true },
          { key: 'starboardTemperature', label: 'Starboard Temp.', minWidth: 100, visible: true },
          { key: 'starboardDensity', label: 'Starboard Density', minWidth: 100, visible: true },
          { key: 'temperatureExhaustStarboard', label: 'Starboard Exhaust Temp.', minWidth: 100, visible: true },
          { key: 'temperatureTankStarboard', label: 'Starboard Reservoir Temp.', minWidth: 100, visible: true },
        ],
        ae: [
          { key: 'ae1RunningHours', label: 'AE1 Run. Hours', minWidth: 100, visible: true },
          { key: 'ae1FuelConsumption', label: 'AE1 Fuel Consumption', minWidth: 100, visible: true },
          { key: 'ae2RunningHours', label: 'AE2 Run. Hours', minWidth: 100, visible: true },
          { key: 'ae2FuelConsumption', label: 'AE2 Fuel Consumption', minWidth: 100, visible: true },
          { key: 'ae3RunningHours', label: 'AE3 Run. Hours', minWidth: 100, visible: true },
          { key: 'ae3FuelConsumption', label: 'AE3 Fuel Consumption', minWidth: 100, visible: true },
        ],
      },
      masks: {
        input: 'DD-MM-YYYY HH:mm',
      },
      isLoading: true,
    };
  },
  computed: {
    currAsset() {
      const currMassId = this.$store.getters['filter/currentMassId'];
      const assets = this.$store.getters['asset/assets'];
      if (!assets) return null;

      return assets.find((asset) => asset.massId === currMassId);
    },
    currAssetSummary() {
      if (!this.currAsset?.summary) return null;

      return {
        series: buildFmsSeries({
          summary: this.currAsset.summary,
          dateRange: this.dateRange,
          interval: this.interval,
          includeTemperature: true,
          temperatureKey: 'temperatureData',
          roundingStrategy: 'legacy-hour',
          normalizeHourlyTimestamps: this.interval === 'hour',
          dataSort: this.dataSort,
        }),
      };
    },
    mappedData() {
      const data = [];

      this.currAssetSummary.series.forEach((series) => {
        data.push({
          timestamp: series.timestamp,
          dateTime: moment(series.timestamp * 1000).format('DD-MM-YYYY, HH:mm'),
          coordinate: typeof series?.gpsData?.latitude !== 'undefined' && typeof series?.gpsData?.longitude !== 'undefined'
            ? `${this.convertDecimalToDMS(series?.gpsData?.latitude, true)}, ${this.convertDecimalToDMS(series?.gpsData?.longitude, false)}`
            : '-',
          // eslint-disable-next-line no-nested-ternary
          speed: typeof series?.gpsData?.speed !== 'undefined'
            ? this.unit.speed === 'knot'
              ? (series?.gpsData?.speed * 0.539957).toFixed(2) : series?.gpsData?.speed.toFixed(2)
            : '-',
          heading: typeof series?.gpsData?.track !== 'undefined' ? series?.gpsData?.track.toFixed(2) : '-',
          // eslint-disable-next-line no-nested-ternary
          cruise: typeof series?.gpsData?.additional?.SUM_dlgpDistance_3 !== 'undefined'
            ? this.unit.distance === 'nm'
              ? (series?.gpsData?.additional?.SUM_dlgpDistance_3 * 0.000539957).toFixed(2)
              : (series?.gpsData?.additional?.SUM_dlgpDistance_3 * 0.001).toFixed(2)
            : '-',
          portRpm: typeof series?.rpmData?.PORT?.rpm !== 'undefined' ? series?.rpmData?.PORT?.rpm.toFixed(2) : '-',
          portRunningHours: typeof series?.rpmData?.PORT?.additional?.COUNTGT_dlrpRpm_1 !== 'undefined'
            ? toHHMMSS(series?.rpmData?.PORT?.additional?.COUNTGT_dlrpRpm_1 * 60)
            : '-',
          portVolumeInventory: typeof series?.fmData?.PORT_IN?.additional?.FIRST_dlfmVolInventory_0 !== 'undefined'
            ? series?.fmData?.PORT_IN?.additional?.FIRST_dlfmVolInventory_0.toFixed(2)
            : '-',
          portDensity: typeof series?.fmData?.PORT_IN?.density !== 'undefined' ? series?.fmData?.PORT_IN?.density.toFixed(2) : '-',
          portTemperature: typeof series?.fmData?.PORT_IN?.temperature !== 'undefined'
            ? series?.fmData?.PORT_IN?.temperature.toFixed(2)
            : '-',
          portVolumeFlowrate: typeof series?.fmData?.PORT_IN?.volumeFlowrate !== 'undefined'
            ? series?.fmData?.PORT_IN?.volumeFlowrate.toFixed(2)
            : '-',
          starboardVolumeInventory: typeof series?.fmData?.STARBOARD_IN?.additional?.FIRST_dlfmVolInventory_0 !== 'undefined'
            ? series?.fmData?.STARBOARD_IN?.additional?.FIRST_dlfmVolInventory_0.toFixed(2)
            : '-',
          starboardDensity: typeof series?.fmData?.STARBOARD_IN?.density !== 'undefined' ? series?.fmData?.STARBOARD_IN?.density.toFixed(2) : '-',
          starboardVolumeFlowrate: typeof series?.fmData?.STARBOARD_IN?.volumeFlowrate !== 'undefined'
            ? series?.fmData?.STARBOARD_IN?.volumeFlowrate.toFixed(2)
            : '-',
          starboardTemperature: typeof series?.fmData?.STARBOARD_IN?.temperature !== 'undefined'
            ? series?.fmData?.STARBOARD_IN?.temperature.toFixed(2)
            : '-',
          starboardRpm: typeof series?.rpmData?.STARBOARD?.rpm !== 'undefined' ? series?.rpmData?.STARBOARD?.rpm.toFixed(2) : '-',
          starboardRunningHours: typeof series?.rpmData?.STARBOARD?.additional?.COUNTGT_dlrpRpm_1 !== 'undefined'
            ? toHHMMSS(series?.rpmData?.STARBOARD?.additional?.COUNTGT_dlrpRpm_1 * 60)
            : '-',
          ae1RunningHours: typeof series?.aeData?.AE1?.runningSeconds !== 'undefined'
            ? toHHMMSS(series?.aeData?.AE1?.runningSeconds)
            : '-',
          ae1FuelConsumption: typeof series?.aeData?.AE1?.fuelConsumption !== 'undefined'
            ? series?.aeData?.AE1?.fuelConsumption.toFixed(2)
            : '-',
          ae2RunningHours: typeof series?.aeData?.AE2?.runningSeconds !== 'undefined'
            ? toHHMMSS(series?.aeData?.AE2?.runningSeconds)
            : '-',
          ae2FuelConsumption: typeof series?.aeData?.AE2?.fuelConsumption !== 'undefined'
            ? series?.aeData?.AE2?.fuelConsumption.toFixed(2)
            : '-',
          ae3RunningHours: typeof series?.aeData?.AE3?.runningSeconds !== 'undefined'
            ? toHHMMSS(series?.aeData?.AE3?.runningSeconds)
            : '-',
          ae3FuelConsumption: typeof series?.aeData?.AE3?.fuelConsumption !== 'undefined'
            ? series?.aeData?.AE3?.fuelConsumption.toFixed(2)
            : '-',
          temperatureExhaustPort: typeof series?.temperatureData?.EXHAUST_TEMPERATURE?.PORT?.[0]?.additional?.AVG_dltmTemp_2 !== 'undefined'
            ? series?.temperatureData?.EXHAUST_TEMPERATURE?.PORT?.[0]?.additional?.AVG_dltmTemp_2.toFixed(2)
            : '-',
          temperatureExhaustStarboard: typeof series?.temperatureData?.EXHAUST_TEMPERATURE?.STARBOARD?.[0]?.additional?.AVG_dltmTemp_2 !== 'undefined'
            ? series?.temperatureData?.EXHAUST_TEMPERATURE?.STARBOARD?.[0]?.additional?.AVG_dltmTemp_2.toFixed(2)
            : '-',
          temperatureTankPort: typeof series?.temperatureData?.TEMPERATURE?.PORT?.[0]?.additional?.AVG_dltmTemp_2 !== 'undefined'
            ? series?.temperatureData?.TEMPERATURE?.PORT?.[0]?.additional?.AVG_dltmTemp_2.toFixed(2)
            : '-',
          temperatureTankStarboard: typeof series?.temperatureData?.TEMPERATURE?.STARBOARD?.[0]?.additional?.AVG_dltmTemp_2 !== 'undefined'
            ? series?.temperatureData?.TEMPERATURE?.STARBOARD?.[0]?.additional?.AVG_dltmTemp_2.toFixed(2)
            : '-',
        });
      });
      return data;
    },
    dateRange: {
      get() {
        const { start, end } = this.$store.getters['filter/date'];
        const startDate = new Date(moment(start).valueOf());
        const endDate = new Date(moment(end).valueOf());

        return { start: startDate, end: endDate };
      },
      set(val) {
        const start = moment(val.start);
        const end = moment(val.end);

        this.$store.commit('filter/SET_DATE', { start, end });
      },
    },
    ...mapState('asset', ['assets']),
    ...mapState('filter', {
      currentMassId: (state) => state.currentMassId,
      interval: (state) => state.interval,
      rangeType: (state) => state.rangeType,
    }),
    selectedInterval: {
      get() {
        return this.interval;
      },
      set(val) {
        this.$store.dispatch('filter/setInterval', val);
      },
    },
    selectedAsset: {
      get() {
        return this.currentMassId;
      },
      set(val) {
        this.$store.dispatch('filter/setCurrentMassId', val);
      },
    },
    selectedRangeType: {
      get() {
        return this.rangeType;
      },
      set(val) {
        this.$store.dispatch('filter/setRangeType', val);
      },
    },
    dataColumns() {
      return Object.values(this.columns).flat();
    },
    columnsForModal() {
      // eslint-disable-next-line no-unused-vars
      const { dateTime, ...rest } = this.defaultColumns;
      return rest;
    },
    visibleColumns() {
      return this.displayColumns.filter((col) => col.visible);
    },
  },
  methods: {
    toHHMMSS,
    convertDecimalToDMS(decimal, isLatitude) {
      const degrees = Math.floor(Math.abs(decimal));
      const minutesDecimal = (Math.abs(decimal) - degrees) * 60;
      const minutes = Math.floor(minutesDecimal);
      const seconds = Math.round((minutesDecimal - minutes) * 60);

      // eslint-disable-next-line no-nested-ternary
      const direction = decimal >= 0
        ? (isLatitude ? 'N' : 'E')
        : (isLatitude ? 'S' : 'W');

      return `${degrees}° ${minutes}' ${seconds}'' ${direction}`;
    },
    moment(date) {
      return moment(date);
    },

    async onApplyFilter() {
      this.isLoading = true;
      try {
        // await this.$store.dispatch('filter/setDate', this.dateRange);
        await this.$store.dispatch('filter/setCurrentMassId', this.selectedAsset || this.currentMassId);
        await this.$store.dispatch('filter/setInterval', this.selectedInterval || this.interval);

        await this.$store.dispatch('asset/fetchSummary', {
          start: this.dateRange.start,
          end: this.dateRange.end,
          interval: this.interval,
          massId: this.currentMassId,
        });
      } catch (err) {
        console.error(err);
      } finally {
        this.isLoading = false;
      }
    },

    async onDownloadExcel() {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Data');

      const visibleColumns = this.dataColumns.filter((col) => col.visible);

      // Set header Excel berdasarkan kolom yang visible dan terurut
      worksheet.columns = visibleColumns.map((col) => ({
        header: col.label,
        key: col.key,
        width: 25, // Lebar kolom bisa disesuaikan
      }));

      // Tambahkan data baris dari mappedData
      this.mappedData.forEach((row) => {
        const rowData = {};
        visibleColumns.forEach((col) => {
          rowData[col.key] = row[col.key] ?? '-';
        });
        worksheet.addRow(rowData);
      });

      const filename = `${this.currAsset?.massName} - ${moment(this.dateRange.start).format('DD-MM-YYYY HH:mm')} - ${moment(this.dateRange.end).format('DD-MM-YYYY HH:mm')}`;

      // Menyimpan file Excel
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(blob, `${filename}.xlsx`);
    },

    showShowHideModal() {
      this.$bvModal.show('show-hide-column-modal');
    },

    hideShowHideModal() {
      this.$bvModal.hide('show-hide-column-modal');
    },

    onDragStart(index) {
      this.dragSrcIndex = index;
    },

    onDrop(targetIndex) {
      if (this.dragSrcIndex === null || this.dragSrcIndex === targetIndex) {
        this.dragSrcIndex = null;
        return;
      }
      if (this.dragSrcIndex === 0 || targetIndex === 0) {
        this.dragSrcIndex = null;
        return;
      }

      // Langsung manipulasi 'displayColumns'
      const draggedCol = this.displayColumns.splice(this.dragSrcIndex, 1)[0];
      this.displayColumns.splice(targetIndex, 0, draggedCol);

      this.dragSrcIndex = null;
    },

    onDragEnd() {
      this.dragSrcIndex = null;
    },

    openIndexedDB() {
      return new Promise((resolve, reject) => {
        const request = indexedDB.open('mantraz');

        request.onupgradeneeded = function handleUpgrade(event) {
          const db = event.target.result;
          if (!db.objectStoreNames.contains('columns')) {
            db.createObjectStore('columns', { keyPath: 'id' });
          }
        };

        request.onsuccess = function handleSuccess(event) {
          resolve(event.target.result);
        };

        request.onerror = function handleError(event) {
          const message = `IndexedDB error: ${event?.target?.error?.message || event?.target?.errorCode || 'Unknown error'}`;
          reject(new Error(message));
        };
      });
    },

    saveColumns(columns) {
      this.openIndexedDB().then((db) => {
        const tx = db.transaction('columns', 'readwrite');
        const store = tx.objectStore('columns');
        // Simpan array 'displayColumns'
        store.put({ id: 1, columns });
      }).catch((err) => {
        console.error('Failed to open IndexedDB:', err.message);
      });
    },

    loadColumnsFromIndexedDB() {
      return this.openIndexedDB().then((db) => new Promise((resolve, reject) => {
        const tx = db.transaction('columns', 'readonly');
        const store = tx.objectStore('columns');
        const request = store.get(1);

        request.onsuccess = function handleGetSuccess(event) {
          resolve(event.target.result?.columns || null);
        };

        request.onerror = function handleGetError(evt) {
          const message = evt?.target?.error?.message || 'Failed to load columns';
          reject(new Error(message));
        };
      })).catch((err) => {
        console.error('Failed to open IndexedDB:', err.message);
      });
    },

    resetColumns() {
      // Reset 'displayColumns' dengan meratakannya dari 'defaultColumns'
      this.displayColumns = Object.values(this.defaultColumns).flat();
    },

    /**
     * Menampilkan semua kolom.
     */
    showAllColumns() {
      // Menambahkan tanda kurung pada argumen arrow function
      Object.values(this.columns).forEach((categoryColumns) => {
        categoryColumns.forEach((col) => {
          col.visible = true;
        });
      });
    },

    /**
     * Menyembunyikan semua kolom, kecuali kolom 'dateTime' yang krusial.
     */
    hideAllColumns() {
      // Menambahkan tanda kurung pada argumen arrow function
      Object.values(this.columns).forEach((categoryColumns) => {
        categoryColumns.forEach((col) => {
          if (col.key !== 'dateTime') {
            col.visible = false;
          }
        });
      });
    },

    /**
     * Memeriksa apakah semua kolom dalam satu kategori sedang ditampilkan.
     * @param {string} categoryKey - Kunci kategori (misal: 'gps', 'port').
     * @returns {boolean} - True jika semua kolom visible.
     */
    isCategoryChecked(categoryKey) {
      return this.columns[categoryKey].every((col) => col.visible);
    },

    /**
     * Menampilkan atau menyembunyikan semua kolom dalam satu kategori.
     * Jika ada satu saja yang tersembunyi, maka akan menampilkan semua.
     * Jika semua sudah tampil, maka akan menyembunyikan semua.
     * @param {string} categoryKey - Kunci kategori yang akan di-toggle.
     */
    toggleCategory(categoryKey) {
      // Dapatkan semua 'key' untuk kolom dalam kategori ini dari sumber utama
      const categoryColumnKeys = this.defaultColumns[categoryKey].map((c) => c.key);

      // Periksa apakah ada kolom dalam kategori ini yang saat ini tidak terlihat
      const shouldShow = this.displayColumns
        .filter((c) => categoryColumnKeys.includes(c.key))
        .some((c) => !c.visible);

      // Buat array baru dengan visibilitas yang diperbarui untuk memicu reaktivitas Vue
      this.displayColumns = this.displayColumns.map((col) => {
        // Jika kolom ini termasuk dalam kategori yang di-toggle
        if (categoryColumnKeys.includes(col.key)) {
          // Kembalikan objek kolom baru dengan properti 'visible' yang diperbarui
          return { ...col, visible: shouldShow };
        }
        // Jika tidak, kembalikan kolom seperti semula
        return col;
      });
    },

    /**
     * Memigrasikan struktur kolom lama (array) ke struktur baru (objek)
     * dan mengatur data kolom komponen.
     * @param {Array|Object} loadedColumns - Kolom yang dimuat dari IndexedDB.
     */
    migrateAndSetColumns(loadedColumns) {
      if (!loadedColumns) {
        // Jika tidak ada data tersimpan, gunakan default
        this.resetColumns();
        return;
      }

      // Jika data tersimpan adalah array (struktur baru), langsung gunakan
      if (Array.isArray(loadedColumns)) {
        this.displayColumns = loadedColumns;
      } else {
        // Jika data tersimpan adalah objek (struktur lama), lakukan migrasi
        console.warn('Old column structure detected. Migrating...');
        const oldVisibilityMap = new Map(Object.values(loadedColumns).flat().map((col) => [col.key, col.visible]));
        const newColumns = Object.values(this.defaultColumns).flat();
        newColumns.forEach((col) => {
          if (oldVisibilityMap.has(col.key)) {
            // eslint-disable-next-line no-param-reassign
            col.visible = oldVisibilityMap.get(col.key);
          }
        });
        this.displayColumns = newColumns;
      }
    },

    findColumnInDisplay(key) {
      return this.displayColumns.find((c) => c.key === key) || {};
    },

    toggleColumnVisibility(key) {
      const column = this.findColumnInDisplay(key);
      if (column) {
        column.visible = !column.visible;
      }
    },
  },
  watch: {
    displayColumns: { // Amati 'displayColumns'
      handler(newColumns) {
        this.saveColumns(newColumns);
      },
      deep: true,
    },
  },
  async mounted() {
    const interval = this.$store.getters['filter/interval'];
    const date = this.$store.getters['filter/date'];

    let currentMassId = this.$store.getters['filter/currentMassId'];
    let assets = this.$store.getters['asset/assets'];

    if (assets.length === 0) {
      await this.$store.dispatch('asset/getAssetsData');
      await this.$store.dispatch('asset/getAssetsDevices');
      await this.$store.dispatch('asset/getLastData');
      assets = this.$store.getters['asset/assets'];
    }

    if (currentMassId === null) {
      await this.$store.dispatch('filter/setCurrentMassId', assets[0].massId);
      currentMassId = this.$store.getters['filter/currentMassId'];
    }

    await this.$store.dispatch('asset/fetchSummary', {
      start: date.start,
      end: date.end,
      interval,
      massId: currentMassId,
    });

    this.loadColumnsFromIndexedDB()
      .then((storedColumns) => {
        // Metode ini sekarang menangani migrasi jika diperlukan
        this.migrateAndSetColumns(storedColumns);
      })
      .catch((err) => {
        console.warn('Could not load columns from IndexedDB:', err.message);
        // Jika gagal, reset ke default
        this.resetColumns();
      });

    this.isLoading = false;
  },
};

</script>

<style scoped>
.flex-container {
  display: flex;
  justify-content: space-between;
}

.flex-item {
  /* padding: 10px 20px; */
  margin: 0 5px;
}

@media screen and (max-width: 600px) {
  .flex-container {
    flex-direction: column;
  }

  .flex-item {
    width: 100%;
  }
}

.sticky-column {
  position: sticky;
  left: 0;
}

.sticky-column:nth-child(odd) {
  background-color: #f5f5f5 !important;
}

.sticky-column:nth-child(even) {
  background-color: #f5f5f5 !important;
}

.draggable-column {
  cursor: move;
  user-select: none;
}

.draggable-column.dragging {
  opacity: 0.5;
}

.table {
  border-collapse: collapse;
}

th {
  position: sticky !important;
  top: 0 !important;
  border: 1px solid !important;
  padding: 8px !important;
  text-align: center !important;
  vertical-align: middle !important;
  font-size: 14.5px !important;
  background-color: #767476 !important;
  color: #FAFAFA !important;
}

th:first-child {
  position: sticky !important;
  left: 0 !important;
  z-index: 5;
}
</style>
